import { useTranslation } from '@invision/core';
import { styled } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import React, { useState } from 'react';
import { colors } from '../../constants/colors';
import { Button, LinkButton } from '../../inputs/Button';
import { Box } from '../../layout/Box';
import { ErrorIcon, Icon, InfoIcon, SuccessIcon } from '../Icon';

export interface MessageProps {
  messages: string[];
  variant: 'success' | 'warning' | 'error' | 'info';
  showButton?: boolean;
  buttonText?: string;
  moreMessage?: string;
  onButtonClick?: () => void;
  containerProps?: { sx?: SystemStyleObject };
}

const ButtonStyled = styled(Button)`
  background-color: ${({ theme }) => theme.palette.warning.main};
  color: ${(props) => `${props.theme.palette.canvas.white}`};
  margin-left: auto;
  border: 1px solid ${(props) => `${props.theme.palette.canvas.white}`};
  &:hover {
    background-color: ${(props) => `${props.theme.palette.canvas.white}`};
    color: ${colors.states.warning};
  }
`;

const MoreMessageStyled = styled(Box)`
  background-color: ${({ theme }) => theme.palette.warning.light};
  color: ${(props) => `${props.theme.palette.canvas.white}`};
`;

const LinkButtonStyled = styled(LinkButton)`
  color: ${(props) => `${props.theme.palette.canvas.white}`};
`;

export function Message({
  variant,
  messages = [],
  showButton = false,
  buttonText,
  moreMessage,
  containerProps,
  onButtonClick
}: MessageProps) {
  const { t } = useTranslation();
  const [isMoreMessageOpen, setIsMoreMessageOpen] = useState<boolean>(false);
  function getGlyph() {
    switch (variant) {
      case 'success':
        return SuccessIcon;
      case 'warning':
        return ErrorIcon;
      case 'error':
        return ErrorIcon;
      case 'info':
        return InfoIcon;
      default:
        return SuccessIcon;
    }
  }

  return messages.length ? (
    <Box
      data-cy="message-messageContainer"
      sx={({ spacing, palette }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(1),
        padding: 2,
        color: (() => {
          switch (variant) {
            case 'error':
              return palette.error.main;
            case 'info':
              return palette.info.main;
            default:
              return palette.canvas.light;
          }
        })(),
        backgroundColor: (() => {
          switch (variant) {
            case 'success':
              return colors.states.success;
            case 'warning':
              return colors.states.warning;
            case 'error':
              return palette.error.light;
            case 'info':
              return palette.info.light;
            default:
              return colors.states.success;
          }
        })(),
        borderRadius: spacing(0.5),
        ...(containerProps?.sx || {})
      })}
    >
      {messages.map((message, index) => (
        <Box
          data-cy={`messageContainer--${index}`}
          key={message}
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center'
          }}
        >
          <Icon glyph={getGlyph()} />
          {message}

          {moreMessage && (
            <LinkButtonStyled
              data-cy="info-link"
              onClick={() => setIsMoreMessageOpen(!isMoreMessageOpen)}
            >
              {isMoreMessageOpen ? t('Less Info') : t('More Info')}
            </LinkButtonStyled>
          )}
          {showButton && (
            <ButtonStyled
              data-cy="message-button"
              onClick={() => !!onButtonClick && onButtonClick()}
            >
              {buttonText}
            </ButtonStyled>
          )}
        </Box>
      ))}
      {isMoreMessageOpen && (
        <MoreMessageStyled sx={{ m: 0, p: 2 }} data-cy="more-message">
          {moreMessage}
        </MoreMessageStyled>
      )}
    </Box>
  ) : null;
}
